import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../../components/FeatherIcon'
import { Spinner } from '../../../components/Spinner'
import { useMediaSrc } from '../../../library/useMediaSrc'
import { OfHtml } from '../../OfHtml'
import { ConfirmModal, ErrorLine } from '../../settings/kit'
import { useLoad } from '../../settings/kitHooks'
import { errorText, type StreamingCtx } from './context'
import { GO_LIVE_URL, canGoLiveSoon, groupEvents, money, parsePage, parseScheduleEvent, scheduledEventsQuery, type ScheduleEvent } from './streamingData'

/*
 * The right sidebar OnlyFans shows beside "New stream": SUBSCRIPTION and
 * SCHEDULED EVENTS (FeedSidebar). Its search box and promo banners are left out.
 */

/** SettingsSubscription widget: "Subscription price and promotions" → Settings > Subscription. */
export function SubscriptionCard({ ctx }: { ctx: StreamingCtx }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const { me } = ctx
  let line = t('subscription.free')
  if (me.subscribePrice > 0) {
    line = t('subscription.price', { price: money(me.subscribePrice) })
    if (me.bundles) line += `, ${t('subscription.bundles', { count: me.bundles })}`
  }
  return (
    <section className="ofst-widget">
      <h3 className="ofst-widget-title">{t('subscription.title')}</h3>
      <button type="button" className="ofst-widget-link" onClick={() => ctx.openPage('settings', 's=subscription')}>
        <span className="oset-row-text">
          <span className="oset-row-label">{t('subscription.row')}</span>
          <span className="oset-row-sub">{line}</span>
        </span>
        <FeatherIcon name="chevron-right" size={24} className="oset-chevron" />
      </button>
    </section>
  )
}

const TYPE_ICON: Record<ScheduleEvent['type'], string> = { post: 'file-text', chat: 'message-square', stream: 'radio' }

/**
 * ScheduledEventsWidget: the next 4 scheduled posts, messages and streams.
 * A stream also gets the Queue's own menu (edit, go live, delete).
 */
export function ScheduledEvents({ ctx, referer, editingId }: { ctx: StreamingCtx; referer: string; editingId?: string }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const events = useLoad(async () => parsePage(await ctx.of.get('schedules', scheduledEventsQuery(), referer), parseScheduleEvent).list, [ctx.of, referer])
  const [deleting, setDeleting] = useState<ScheduleEvent | null>(null)
  const openQueue = () => ctx.openPage('queue')

  let body
  if (events.data) {
    const { upcoming, days } = groupEvents(events.data)
    body =
      upcoming.length || days.length ? (
        <>
          {upcoming.map((e) => (
            <UpcomingStream key={e.id} event={e} />
          ))}
          <div className="ofst-agenda">
            {days.map((day) => (
              <div key={day.date} className="ofst-agenda-day">
                <button type="button" className="ofst-agenda-date" onClick={openQueue}>
                  <strong>{agendaDate(day.date, { month: 'short', day: 'numeric' })}</strong>
                  <span>{agendaDate(day.date, { weekday: 'short' })}</span>
                </button>
                <div className="ofst-agenda-events">
                  {day.events.map((e) => (
                    <AgendaEvent
                      key={e.id}
                      ctx={ctx}
                      event={e}
                      onOpen={openQueue}
                      onEdit={() => ctx.go(`edit=${e.entityId}`)}
                      onDelete={() => setDeleting(e)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <p className="ofst-widget-hint">{t('events.hint')}</p>
          <p className="ofst-widget-empty">
            <FeatherIcon name="calendar" size={28} />
            <span>{t('events.none')}</span>
          </p>
        </>
      )
  } else if (events.loading) {
    body = (
      <div className="ofst-more">
        <Spinner size={24} />
      </div>
    )
  } else {
    body = <ErrorLine onRetry={events.reload}>{t('loadFailed', { error: events.error || '—' })}</ErrorLine>
  }

  return (
    <section className="ofst-widget">
      <h3 className="ofst-widget-title">{t('events.title')}</h3>
      {body}
      <div className="ofst-widget-foot">
        <button type="button" className="oset-btn oset-btn--flat oset-btn--sm" onClick={openQueue}>
          {t('events.viewQueue')}
        </button>
      </div>
      {deleting && (
        <ConfirmModal
          text={t('events.confirmDelete')}
          confirm={t('events.deleteBtn')}
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            try {
              await ctx.of.send('DELETE', `streams/${deleting.entityId}`, referer)
            } catch (e) {
              throw new Error(errorText(e, t('error')))
            }
            ctx.say(t('events.deleted'))
            if (editingId && editingId === deleting.entityId) ctx.go('', true)
            else events.reload()
          }}
        />
      )}
    </section>
  )
}

function agendaDate(ymd: string, opts: Intl.DateTimeFormatOptions): string {
  const d = new Date(`${ymd}T12:00:00`)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, opts)
}

const eventTime = (iso: string) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }).toLowerCase()
}

/** UpcomingStream: a stream starting within 30 minutes, with OnlyFans' GO LIVE (onlyfans.com only). */
function UpcomingStream({ event }: { event: ScheduleEvent }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const thumb = useMediaSrc(event.thumbs[0] ?? '')
  return (
    <div className="ofst-upcoming">
      {thumb.src && <img className="ofst-upcoming-thumb" src={thumb.src} alt="" />}
      <div className="ofst-upcoming-info" ref={thumb.ref as React.RefObject<HTMLDivElement>}>
        <div className="ofst-upcoming-time">
          {eventTime(event.publishDateTime)} <FeatherIcon name="radio" size={14} />
        </div>
        {event.text && <OfHtml html={event.text} className="ofst-upcoming-text" />}
      </div>
      <button type="button" className="oset-btn oset-btn--flat oset-btn--sm" disabled title={t('goLiveNote')}>
        {t('goLive')}
      </button>
      <a className="ofst-upcoming-of" href={GO_LIVE_URL} target="_blank" rel="noopener noreferrer">
        {t('openOnOf')}
      </a>
    </div>
  )
}

function AgendaEvent({ ctx, event, onOpen, onEdit, onDelete }: { ctx: StreamingCtx; event: ScheduleEvent; onOpen: () => void; onEdit: () => void; onDelete: () => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const thumb = useMediaSrc(event.thumbs[0] ?? '')
  return (
    <div className={`ofst-agenda-item is-${event.type}`}>
      <button type="button" className="ofst-agenda-content" onClick={onOpen} ref={thumb.ref as React.RefObject<HTMLButtonElement>}>
        <span className="ofst-agenda-details">
          <span className="ofst-agenda-meta">
            <span className="ofst-agenda-time">{eventTime(event.publishDateTime)}</span>
            <FeatherIcon name={TYPE_ICON[event.type]} size={16} className="ofst-agenda-icon" aria-label={t(`events.${event.type}`)} />
            {event.price > 0 && <span>{money(event.price)}</span>}
          </span>
          {event.text && <span className="ofst-agenda-text">{plain(event.text)}</span>}
        </span>
        {thumb.src ? <img className="ofst-agenda-thumb" src={thumb.src} alt="" /> : event.mediaPending ? <Spinner size={18} /> : null}
      </button>
      {event.type === 'stream' && <StreamMenu ctx={ctx} event={event} onEdit={onEdit} onDelete={onDelete} />}
    </div>
  )
}

const plain = (html: string) =>
  html
    .replace(/<br\s*\/?>|<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim()

/** ScheduleEventDropdown for a stream: Edit live stream, Go live right now, Delete live stream. */
function StreamMenu({ ctx, event, onEdit, onDelete }: { ctx: StreamingCtx; event: ScheduleEvent; onEdit: () => void; onDelete: () => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => !root.current?.contains(e.target as Node) && setOpen(false)
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', esc)
    }
  }, [open])
  const soon = canGoLiveSoon(event.publishDateTime)
  return (
    <div className="ofst-menu" ref={root}>
      <button type="button" className="oset-head-btn ofst-menu-btn" aria-label={t('events.more')} title={t('events.more')} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <FeatherIcon name="more-horizontal" size={20} />
      </button>
      {open && (
        <div className="ofst-menu-list" role="menu">
          <button
            type="button"
            role="menuitem"
            disabled={!ctx.canWrite}
            onClick={() => {
              setOpen(false)
              onEdit()
            }}
          >
            {t('events.edit')}
          </button>
          <button type="button" role="menuitem" disabled title={soon ? t('goLiveNote') : undefined}>
            {t('events.goLiveNow')}
          </button>
          <hr />
          <button
            type="button"
            role="menuitem"
            className="is-danger"
            disabled={!ctx.canWrite}
            onClick={() => {
              setOpen(false)
              onDelete()
            }}
          >
            {t('events.delete')}
          </button>
        </div>
      )}
    </div>
  )
}
